import { useState, useEffect, useRef } from 'react';
import { itemsApi, brandsApi, lookupApi, suppliersApi } from '../api';
import { useData, submitForm, useSubmit } from '../hooks/useData';
import { Spinner, Modal, PageHeader, Field, Empty, EditBtn, CancelBtn, ImgPreview, Icons } from '../components/common';
import toast from 'react-hot-toast';
import JsBarcode from 'jsbarcode';
import { getBDDate } from '../utils/dateUtils';

const EMPTY = {
  brand_id: '', color_variation_id: '', item_name: '', description: '',
  price: '', date_created: getBDDate(), active_yes_no: 1
};

const EMPTY_CALC = {
  qty:'', pkr_cost:'', exchange_rate:'', unit_cost:'',
  kg_rate:'', weight:'', delivery_cost:'', other_cost:''
};

function BarcodeDisplay({ seed }) {
  const ref = useRef();
  useEffect(() => {
    if (!seed || !ref.current) return;
    try {
      JsBarcode(ref.current, seed.padStart(6,'0'), {
        format: 'CODE128', width: 1.5, height: 36, displayValue: true, fontSize: 10, margin: 4
      });
    } catch {}
  }, [seed]);
  if (!seed) return null;
  return <svg ref={ref} style={{ maxWidth: 130 }} />;
}

export default function ItemsPage() {
  const { data, loading, reload } = useData(itemsApi.list);
  const { data: brands }          = useData(brandsApi.active);
  const { data: suppliers }        = useData(suppliersApi.list);
  const { data: colors }          = useData(lookupApi.colors);
  const [modal, setModal]         = useState(null);
  const [doSubmit, saving]         = useSubmit();
  const [form, setForm]           = useState(EMPTY);
  const [imgs, setImgs]           = useState({});
  const [search, setSearch]       = useState('');
  const [errors, setErrors]       = useState({});
  // Price calculator state (UI only, not saved to DB)
  const [calc, setCalc]           = useState(EMPTY_CALC);
  const [calcResult, setCalcResult] = useState(null); // cost per unit after all charges
  const [marginPct, setMarginPct] = useState('');     // % to add on top

  const openAdd  = ()    => { setForm(EMPTY); setImgs({}); setErrors({}); setCalc(EMPTY_CALC); setCalcResult(null); setMarginPct(''); setModal('add'); };
  const openEdit = (row) => {
    const brand = brands.find(b => String(b.id) === String(row.brand_id));
    setForm({
      ...row,
      supplier_id:   brand?.supplier_id   || row.supplier_id   || '',
      supplier_name: brand?.supplier_name || row.supplier_name || '',
    });
    setImgs({}); setErrors({});
    setCalc(EMPTY_CALC);
    setCalcResult(null); setMarginPct('');
    setModal('edit');
  };
  const close    = ()    => { setModal(null); setErrors({}); };
  const set = e => {
    const { name, value } = e.target;
    if (name === 'brand_id') {
      // Auto-fill supplier when brand is selected
      const brand = brands.find(b => String(b.id) === String(value));
      setForm(f => ({
        ...f,
        brand_id:      value,
        supplier_id:   brand?.supplier_id   || f.supplier_id,
        supplier_name: brand?.supplier_name || f.supplier_name,
      }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
    setErrors(er => ({ ...er, [name]: '' }));
  };

  // Price calculator
  const setC = e => setCalc(v => ({ ...v, [e.target.name]: e.target.value }));

  // Total Unit Cost (BDT) = Total Cost (PKR) × Exchange Rate — auto-filled
  useEffect(() => {
    const pkr  = parseFloat(calc.pkr_cost)      || 0;
    const rate = parseFloat(calc.exchange_rate) || 0;
    const val  = pkr * rate;
    setCalc(c => ({ ...c, unit_cost: val ? val.toFixed(2) : '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc.pkr_cost, calc.exchange_rate]);

  // Delivery Cost = KG/Rate × Weight — auto-filled
  useEffect(() => {
    const kgRate = parseFloat(calc.kg_rate) || 0;
    const weight = parseFloat(calc.weight)  || 0;
    const val    = kgRate * weight;
    setCalc(c => ({ ...c, delivery_cost: val ? val.toFixed(2) : '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc.kg_rate, calc.weight]);

  const handleCalc = () => {
    const qty  = parseFloat(calc.qty)           || 0;
    const unit = parseFloat(calc.unit_cost)     || 0;
    const del  = parseFloat(calc.delivery_cost) || 0;
    const oth  = parseFloat(calc.other_cost)    || 0;
    if (qty <= 0) { alert('Please enter a valid quantity'); return; }
    const costPerUnit = (unit + del + oth) / qty;
    setCalcResult(costPerUnit);
    setMarginPct('');
  };

  const handleApplyMargin = () => {
    if (!calcResult) return;
    const margin = parseFloat(marginPct) || 0;
    const sellingPrice = calcResult * (1 + margin / 100);
    setForm(f => ({ ...f, price: sellingPrice.toFixed(2) }));
  };

  const validate = () => {
    const e = {};
    if (!form.brand_id)           e.brand_id           = 'Please select a brand';
    if (!form.color_variation_id) e.color_variation_id = 'Please select a color/variation';
    if (!form.item_name?.trim())  e.item_name          = 'Item name is required';
    else {
      const nameLower = form.item_name.trim().toLowerCase();
      const dup = data.some(i =>
        i.item_name?.trim().toLowerCase() === nameLower && String(i.id) !== String(form.id)
      );
      if (dup) e.item_name = 'An item with this name already exists';
    }
    if (!form.price || isNaN(form.price) || Number(form.price) < 0) e.price = 'Valid price is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) { toast.error('Please fill in all required fields'); return; }
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => v != null && fd.append(k, v));
    ['image1','image2','image3'].forEach(k => { if (imgs[k]) fd.append(k, imgs[k]); });
    if (modal === 'add') doSubmit(() => itemsApi.create(fd), 'Item added', reload, close);
    else                 doSubmit(() => itemsApi.update(form.id, fd), 'Item updated', reload, close);
  };

  const filtered = data.filter(r =>
    !search ||
    r.item_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.brand_name?.toLowerCase().includes(search.toLowerCase())
  );

  const barcodeSeed = form.brand_id && form.color_variation_id
    ? String(((1000 + parseInt(form.brand_id)) * 1000) + parseInt(form.color_variation_id)).slice(-6).padStart(6,'0')
    : null;

  return (
    <div>
      <PageHeader title="Items" subtitle="Manage your product catalog"
        action={<button className="btn btn-primary btn-sm" onClick={openAdd}>+ Add Item</button>} />

      <div className="card">
        <div className="card-header">
          <div className="search-wrap">
            <Icons.Search size={14} className="search-icon" />
            <input type="text" className="search-input" placeholder="Search by item or brand…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        {loading ? <Spinner /> : (
          <div className="table-wrap">
            {filtered.length === 0 ? <Empty msg="No items found" /> : (
              <table className="responsive-table">
                <thead><tr>
                  <th>Item</th><th>Brand / Color</th>
                  <th>Price</th><th title="Perpetual weighted average cost">Avg Cost</th><th>Status</th><th>Actions</th>
                </tr></thead>
                <tbody>{filtered.map(r => (
                  <tr key={r.id}>
                    <td data-label="Item">
                      <div className="td-primary">{r.item_name}</div>
                      <div style={{ display:'flex', gap:4, marginTop:4, justifyContent:'flex-end' }}>
                        <ImgPreview src={r.image1} small /><ImgPreview src={r.image2} small /><ImgPreview src={r.image3} small />
                      </div>
                    </td>
                    <td data-label="Brand / Color">
                      <div>{r.brand_name}</div>
                      <div className="td-muted">{r.color_variation}</div>
                    </td>
                    <td data-label="Price" className="font-mono">৳{Number(r.price).toLocaleString()}</td>
                    <td data-label="Avg Cost" className="font-mono">৳{Number(r.cost_price).toLocaleString()}</td>
                    <td data-label="Status"><span className={`badge ${r.active_yes_no ? 'badge-active':'badge-inactive'}`}>{r.active_yes_no ? 'Active':'Inactive'}</span></td>
                    <td data-label="Actions"><div className="action-cell">
                      <EditBtn onClick={() => openEdit(r)} />
                      {r.active_yes_no===1 && <CancelBtn onClick={() => submitForm(() => itemsApi.deactivate(r.id),'Deactivated',reload)} />}
                    </div></td>
                  </tr>
                ))}</tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {modal && (
        <Modal title={modal==='add' ? 'Add Item' : 'Edit Item'} onClose={close} size="modal-lg"
          footer={<>
            <button className="btn btn-secondary" onClick={close}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Item'}</button>
          </>}>
          {/* Brand + Supplier (auto-filled) */}
          <div className="form-row form-row-2">
            <Field label="Brand" required>
              <select name="brand_id" value={form.brand_id} onChange={set}
                style={errors.brand_id ? {borderColor:'var(--c-danger)'} : {}}>
                <option value="">Select brand…</option>
                {brands.map(b => <option key={b.id} value={b.id}>{b.brand_name}</option>)}
              </select>
              {errors.brand_id && <span style={{fontSize:'.75rem',color:'var(--c-danger)'}}>{errors.brand_id}</span>}
            </Field>
            <Field label="Supplier">
              <select name="supplier_id" value={form.supplier_id || ''}
                onChange={e => {
                  const sup = suppliers.find(s => String(s.id) === String(e.target.value));
                  setForm(f => ({ ...f, supplier_id: e.target.value, supplier_name: sup?.full_name || '' }));
                }}>
                <option value="">Select supplier…</option>
                {suppliers.filter(s => s.active_yes_no).map(s =>
                  <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            </Field>
          </div>

          <div className="form-row form-row-2">
            <Field label="Color / Variation" required>
              <select name="color_variation_id" value={form.color_variation_id} onChange={set}
                style={errors.color_variation_id ? {borderColor:'var(--c-danger)'} : {}}>
                <option value="">Select color…</option>
                {colors.filter(c=>c.active_yes_no).map(c => <option key={c.id} value={c.id}>{c.color_variation}</option>)}
              </select>
              {errors.color_variation_id && <span style={{fontSize:'.75rem',color:'var(--c-danger)'}}>{errors.color_variation_id}</span>}
            </Field>
            <Field label="Item Name" required>
              <input type="text" name="item_name" value={form.item_name} onChange={set}
                placeholder="e.g. Floral Kurti"
                style={errors.item_name ? {borderColor:'var(--c-danger)'} : {}} />
              {errors.item_name && <span style={{fontSize:'.75rem',color:'var(--c-danger)'}}>{errors.item_name}</span>}
            </Field>
          </div>

          {/* ── Selling Price Calculator ─────────────────────── */}
          <div style={{ margin:'4px 0 16px', padding:'16px 18px', background:'var(--c-accent-tint)', border:'1px solid var(--c-border)', borderLeft:'3px solid var(--c-accent)', borderRadius:'var(--r-md)' }}>
            <div style={{ fontWeight:600, fontSize:'.88rem', color:'var(--c-text)', marginBottom:12 }}>
              Price Calculator <span style={{ fontWeight:400, color:'var(--c-text-3)', fontSize:'.78rem' }}>— optional, fills Selling Price</span>
            </div>

            {/* Step 1: Cost inputs */}
            <div style={{ fontSize:'.75rem', fontWeight:600, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--c-text-3)', marginBottom:8 }}>
              Step 1 — Enter Costs
            </div>

            <div className="form-row form-row-3" style={{ marginBottom:8 }}>
              <Field label="Quantity (pcs)">
                <input type="number" name="qty" value={calc.qty} onChange={setC} min="1" placeholder="e.g. 10" />
              </Field>
              <Field label="Total Cost (PKR)">
                <input type="number" name="pkr_cost" value={calc.pkr_cost} onChange={setC} min="0" step="0.01" placeholder="e.g. 500" />
              </Field>
              <Field label="Exchange Rate">
                <input type="number" name="exchange_rate" value={calc.exchange_rate} onChange={setC} min="0" step="0.01" placeholder="e.g. 3.5" />
              </Field>
            </div>

            <div className="form-row form-row-3" style={{ marginBottom:8 }}>
              <Field label="Total Cost (BDT)">
                <input type="number" value={calc.unit_cost} readOnly placeholder="Auto-calculated"
                  style={{ backgroundColor:'var(--c-bg)', color:'var(--c-text-2)', cursor:'not-allowed' }} />
              </Field>
              <Field label="KG / Rate (৳)">
                <input type="number" name="kg_rate" value={calc.kg_rate} onChange={setC} min="0" step="0.01" placeholder="e.g. 250" />
              </Field>
              <Field label="Weight (KG)">
                <input type="number" name="weight" value={calc.weight} onChange={setC} min="0" step="0.01" placeholder="e.g. 2" />
              </Field>
            </div>

            <div className="form-row form-row-2" style={{ marginBottom:10 }}>
              <Field label="Shipping Charge (৳)">
                <input type="number" value={calc.delivery_cost} readOnly placeholder="Auto-calculated"
                  style={{ backgroundColor:'var(--c-bg)', color:'var(--c-text-2)', cursor:'not-allowed' }} />
              </Field>
              <Field label="Delivery Cost (৳)">
                <input type="number" name="other_cost" value={calc.other_cost} onChange={setC} min="0" step="0.01" placeholder="e.g. 100" />
              </Field>
            </div>

            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom: calcResult !== null ? 12 : 0 }}>
              <button type="button" className="btn btn-secondary" onClick={handleCalc}>
                Calculate
              </button>
            </div>

            {/* Step 2: Result + margin */}
            {calcResult !== null && (
              <>
                <div style={{ padding:'10px 14px', background:'var(--c-surface)', border:'1px solid var(--c-border)', borderRadius:'var(--r-sm)', marginBottom:12, fontSize:'.88rem' }}>
                  <span style={{ color:'var(--c-text-3)' }}>Cost per unit: </span>
                  <strong className="font-mono" style={{ color:'var(--c-text)', fontSize:'1rem' }}>৳{calcResult.toFixed(2)}</strong>
                  <span style={{ color:'var(--c-text-3)', marginLeft:12, fontSize:'.78rem' }}>
                    = ({calc.unit_cost || 0} + {calc.delivery_cost || 0} + {calc.other_cost || 0}) ÷ {calc.qty}
                  </span>
                </div>
                <div style={{ fontSize:'.75rem', fontWeight:600, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--c-text-3)', marginBottom:8 }}>
                  Step 2 — Add Profit Margin
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'flex-end' }}>
                  {[10,20,30,40,50].map(pct => (
                    <button key={pct} type="button"
                      className={`btn btn-sm ${String(marginPct)===String(pct) ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => { setMarginPct(pct); }}>
                      +{pct}%
                    </button>
                  ))}
                  <div style={{ display:'flex', gap:6, alignItems:'center', flex:'0 0 auto' }}>
                    <input type="number" value={marginPct} onChange={e => setMarginPct(e.target.value)}
                      placeholder="Custom %" min="0" step="0.01"
                      style={{ width:100 }} />
                    <span style={{ fontSize:'.82rem', color:'var(--c-text-3)' }}>%</span>
                  </div>
                  <button type="button" className="btn btn-primary btn-sm"
                    onClick={handleApplyMargin} disabled={!marginPct}>
                    Apply → Selling Price
                  </button>
                </div>
                {marginPct && calcResult && (
                  <div style={{ marginTop:10, fontSize:'.84rem', color:'var(--c-text-2)' }}>
                    ৳{calcResult.toFixed(2)} × {(1 + parseFloat(marginPct)/100).toFixed(2)} =
                    <strong style={{ color:'var(--c-accent)', marginLeft:4 }}>
                      ৳{(calcResult*(1+parseFloat(marginPct)/100)).toFixed(2)}
                    </strong>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Selling Price — filled manually or by calculator */}
          <Field label="Selling Price (৳)" required>
            <input type="number" name="price" value={form.price} onChange={set} min="0" step="0.01"
              placeholder="0.00 — enter manually or use calculator above"
              style={errors.price ? {borderColor:'var(--c-danger)'} : {}} />
            {errors.price && <span style={{fontSize:'.75rem',color:'var(--c-danger)'}}>{errors.price}</span>}
          </Field>
          <div className="form-row form-row-2">
            <Field label="Date Created">
              <input type="date" name="date_created" value={form.date_created} onChange={set} />
            </Field>
            {modal==='edit' && (
              <Field label="Status">
                <select name="active_yes_no" value={form.active_yes_no}
                  onChange={e => setForm(f => ({...f, active_yes_no: Number(e.target.value)}))}>
                  <option value={1}>Active</option><option value={0}>Inactive</option>
                </select>
              </Field>
            )}
          </div>
          <Field label="Description">
            <textarea type="text" name="description" value={form.description||''} onChange={set} rows={2} placeholder="Optional product description…" />
          </Field>
          <div className="form-row form-row-3">
            {['image1','image2','image3'].map((k,i) => (
              <Field key={k} label={`Image ${i+1}`}>
                <input type="file" accept="image/*" onChange={e => setImgs(v=>({...v,[k]:e.target.files[0]}))} />
                {form[k] && !imgs[k] && <ImgPreview src={form[k]} small />}
              </Field>
            ))}
          </div>
          {barcodeSeed && (
            <div style={{marginTop:12,padding:'10px 14px',background:'var(--c-bg)',borderRadius:'var(--r-sm)'}}>
              <label style={{marginBottom:8}}>Barcode Preview</label>
              <BarcodeDisplay seed={barcodeSeed} />
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}